'use client';

import { useState, useMemo } from 'react';
import { notFound } from 'next/navigation';
import Link from 'next/link';
import { useQuery } from 'convex/react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { api } from '../../../../convex/_generated/api';

// ── i18n ──────────────────────────────────────────────────────────────────────
const CATALOG_I18N = {
  it: {
    heading1: 'Materiali selezionati',
    heading2: 'per il tuo progetto.',
    count1: 'materiale disponibile',
    countN: 'materiali disponibili',
    updated: 'aggiornato in tempo reale',
    all: 'Tutti',
    inStock: 'Disponibile',
    outOfStock: 'Esaurito',
    mat1: 'materiale',
    matN: 'materiali',
    noResults: 'Nessun materiale con i filtri selezionati.',
    clearFilters: 'Azzera filtri',
    searchPlaceholder: 'Cerca nel catalogo...',
    contact: 'Contatta il fornitore',
    poweredBy: 'Gestito con',
  },
  en: {
    heading1: 'Selected materials',
    heading2: 'for your project.',
    count1: 'material available',
    countN: 'materials available',
    updated: 'updated in real time',
    all: 'All',
    inStock: 'Available',
    outOfStock: 'Out of stock',
    mat1: 'material',
    matN: 'materials',
    noResults: 'No materials match the selected filters.',
    clearFilters: 'Clear filters',
    searchPlaceholder: 'Search catalog...',
    contact: 'Contact supplier',
    poweredBy: 'Managed with',
  },
  es: {
    heading1: 'Materiales seleccionados',
    heading2: 'para tu proyecto.',
    count1: 'material disponible',
    countN: 'materiales disponibles',
    updated: 'actualizado en tiempo real',
    all: 'Todos',
    inStock: 'Disponible',
    outOfStock: 'Agotado',
    mat1: 'material',
    matN: 'materiales',
    noResults: 'Ningún material coincide con los filtros seleccionados.',
    clearFilters: 'Borrar filtros',
    searchPlaceholder: 'Buscar en catálogo...',
    contact: 'Contactar proveedor',
    poweredBy: 'Gestionado con',
  },
  de: {
    heading1: 'Ausgewählte Materialien',
    heading2: 'für Ihr Projekt.',
    count1: 'Material verfügbar',
    countN: 'Materialien verfügbar',
    updated: 'in Echtzeit aktualisiert',
    all: 'Alle',
    inStock: 'Verfügbar',
    outOfStock: 'Vergriffen',
    mat1: 'Material',
    matN: 'Materialien',
    noResults: 'Keine Materialien entsprechen den ausgewählten Filtern.',
    clearFilters: 'Filter zurücksetzen',
    searchPlaceholder: 'Katalog durchsuchen...',
    contact: 'Lieferant kontaktieren',
    poweredBy: 'Verwaltet mit',
  },
  fr: {
    heading1: 'Matériaux sélectionnés',
    heading2: 'pour votre projet.',
    count1: 'matériau disponible',
    countN: 'matériaux disponibles',
    updated: 'mis à jour en temps réel',
    all: 'Tous',
    inStock: 'Disponible',
    outOfStock: 'Épuisé',
    mat1: 'matériau',
    matN: 'matériaux',
    noResults: 'Aucun matériau ne correspond aux filtres sélectionnés.',
    clearFilters: 'Effacer les filtres',
    searchPlaceholder: 'Rechercher dans le catalogue...',
    contact: 'Contacter le fournisseur',
    poweredBy: 'Géré avec',
  },
  tr: {
    heading1: 'Seçilmiş malzemeler',
    heading2: 'projeniz için.',
    count1: 'malzeme mevcut',
    countN: 'malzeme mevcut',
    updated: 'gerçek zamanlı güncellenir',
    all: 'Tümü',
    inStock: 'Mevcut',
    outOfStock: 'Tükendi',
    mat1: 'malzeme',
    matN: 'malzeme',
    noResults: 'Seçilen filtrelere uyan malzeme yok.',
    clearFilters: 'Filtreleri temizle',
    searchPlaceholder: 'Katalogda ara...',
    contact: 'Tedarikçiyle iletişim',
    poweredBy: 'Yönetilen',
  },
} as const;

type Locale = keyof typeof CATALOG_I18N;

function getT(locale: string) {
  return CATALOG_I18N[(locale as Locale) in CATALOG_I18N ? (locale as Locale) : 'en'];
}

// ── Types ─────────────────────────────────────────────────────────────────────
interface PublicItem {
  id: string;
  sku: string;
  name: string;
  item_type: string;
  status: string | null;
  width_cm: number | null;
  height_cm: number | null;
  thickness_cm: number | null;
  unit: string;
  photo_url: string | null;
  category_id: string | null;
  category_name: string | null;
  category_color: string | null;
  category_gradient_from: string | null;
  category_gradient_to: string | null;
  is_active: boolean;
  is_public: boolean;
  current_stock: number;
}

function itemDim(item: PublicItem): string {
  if (item.width_cm && item.height_cm) {
    return `${item.width_cm} × ${item.height_cm}${item.thickness_cm ? ` × ${item.thickness_cm}` : ''} mm`;
  }
  return item.sku;
}

function isInStock(item: PublicItem): boolean {
  return item.current_stock > 0 && (item.status === 'available' || item.item_type === 'lot');
}

function itemSwatch(item: PublicItem): string {
  if (item.category_gradient_from && item.category_gradient_to) {
    return `linear-gradient(135deg, ${item.category_gradient_from}, ${item.category_gradient_to})`;
  }
  return item.category_color ?? '#e7e5e4';
}

// Categoria locale per i filtri (shape minimale usata dal catalogo pubblico).
interface PublicCategory {
  id: string;
  name: string;
  color_hex: string | null;
}

// Adatta un item Convex (camelCase, _id, currentStock) alla shape PublicItem
// legacy usata dalla UI. I campi categoria sono joinati lato server da
// api.catalog.bySlug (categoryName/categoryColorHex/categoryGradient*).
function adaptPublicItem(it: any): PublicItem {
  return {
    id: it._id,
    sku: it.sku,
    name: it.name,
    item_type: it.itemType,
    status: it.status ?? null,
    width_cm: it.widthCm ?? null,
    height_cm: it.heightCm ?? null,
    thickness_cm: it.thicknessCm ?? null,
    unit: it.unit ?? '',
    photo_url: it.photoUrl ?? null, // URL risolto dallo storage nativo Convex
    category_id: it.categoryId ?? null,
    category_name: it.categoryName ?? null,
    category_color: it.categoryColorHex ?? null,
    category_gradient_from: it.categoryGradientFrom ?? null,
    category_gradient_to: it.categoryGradientTo ?? null,
    is_active: it.isActive,
    is_public: it.isPublic,
    current_stock: it.currentStock ?? 0,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────
export function CatalogClient({
  slug,
  locale = 'en',
}: {
  slug: string;
  locale?: string;
}) {
  const T = getT(locale);
  const [activeCat, setActiveCat] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Lettura PUBBLICA reattiva del catalogo (no auth).
  const data = useQuery(api.catalog.bySlug, { slug });

  const workspaceName = data?.workspace.name ?? '';
  const items: PublicItem[] = useMemo(
    () => (data?.items ?? []).map(adaptPublicItem),
    [data],
  );
  // Categorie pubbliche (attive e usate da articoli visibili) da api.catalog.bySlug.
  const categories: PublicCategory[] = useMemo(
    () => (data?.categories ?? []).map((c: any) => ({ id: c.id, name: c.name, color_hex: c.colorHex ?? null })),
    [data],
  );

  const filtered = useMemo(() => {
    let list = items;
    if (activeCat) list = list.filter((i) => i.category_id === activeCat);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((i) => i.name.toLowerCase().includes(q) || i.sku.toLowerCase().includes(q));
    }
    return list;
  }, [items, activeCat, search]);

  const pillBase = 'inline-flex items-center gap-2 px-3.5 py-[7px] rounded-full text-[13px] font-medium whitespace-nowrap transition-all duration-150 cursor-pointer border';
  const pillActive = 'bg-stone-950 dark:bg-zinc-50 text-stone-50 dark:text-zinc-950 border-transparent';
  const pillInactive = 'bg-white dark:bg-zinc-900 text-stone-500 dark:text-zinc-400 border-stone-200 dark:border-zinc-700 hover:text-stone-950 dark:hover:text-zinc-50';

  // Workspace inesistente o catalogo non pubblico → 404 (come il vecchio notFound() server).
  if (data === null) notFound();
  // Caricamento iniziale: nessuno stato visibile finché la query non risolve.
  if (data === undefined) return null;

  return (
    <div>
      {/* Hero */}
      <div className="pt-12 pb-8">
        <div
          className="font-mono text-[12px] tracking-[0.14em] uppercase mb-3 flex items-center gap-2.5"
          style={{ color: 'var(--accent)' }}
        >
          {workspaceName}
          <span className="h-px w-16 bg-stone-300 dark:bg-zinc-700 block" />
        </div>
        <h1 className="m-0 font-bold tracking-[-0.025em] leading-[1.1] text-stone-950 dark:text-zinc-50" style={{ fontSize: 'clamp(32px, 4.5vw, 48px)' }}>
          {T.heading1}<br />{T.heading2}
        </h1>
        <p className="mt-3 text-[17px] leading-[1.55] text-stone-500 dark:text-zinc-400 max-w-[52ch] m-0 mt-3">
          {items.length} {items.length === 1 ? T.count1 : T.countN} · {T.updated}
        </p>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400 dark:text-zinc-500 pointer-events-none" />
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={T.searchPlaceholder}
          className="w-full h-11 pl-9 pr-10 rounded-[10px] border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-[14px] text-stone-950 dark:text-zinc-50 placeholder:text-stone-400 dark:placeholder:text-zinc-500 outline-none focus:border-stone-400 dark:focus:border-zinc-500 transition-colors"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 dark:text-zinc-500 hover:text-stone-700 dark:hover:text-zinc-300"
          >
            <X size={15} />
          </button>
        )}
      </div>

      {/* Filter pills */}
      {categories.length > 0 && (
        <div className="flex items-center gap-2.5 flex-wrap py-4 border-t border-b border-stone-200 dark:border-zinc-800 mb-8 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
          <button
            className={cn(pillBase, activeCat === null ? pillActive : pillInactive)}
            onClick={() => setActiveCat(null)}
          >
            {T.all}
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              className={cn(pillBase, activeCat === cat.id ? pillActive : pillInactive)}
              onClick={() => setActiveCat(activeCat === cat.id ? null : cat.id)}
            >
              {cat.color_hex && (
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ background: cat.color_hex }}
                />
              )}
              {cat.name}
            </button>
          ))}
        </div>
      )}

      {/* Count */}
      <div className="mb-6 text-[13px] text-stone-500 dark:text-zinc-400">
        {filtered.length} {filtered.length === 1 ? T.mat1 : T.matN}
        {activeCat && (
          <button
            onClick={() => setActiveCat(null)}
            className="ml-3 text-stone-950 dark:text-zinc-50 font-medium hover:underline underline-offset-2"
          >
            {T.clearFilters}
          </button>
        )}
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="py-24 text-center text-stone-400 dark:text-zinc-500 text-[15px]">
          {T.noResults}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pb-8">
          {filtered.map((item) => {
            const stocked = isInStock(item);
            return (
              <Link
                key={item.id}
                href={`/c/${slug}/items/${item.id}`}
                className="group flex flex-col overflow-hidden rounded-2xl bg-white dark:bg-zinc-900 border border-stone-200 dark:border-zinc-800 hover:-translate-y-0.5 hover:shadow-md transition-all duration-300"
              >
                {/* Swatch / Photo */}
                <div className="relative overflow-hidden" style={{ aspectRatio: '4/3' }}>
                  {item.photo_url ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={item.photo_url}
                      alt={item.name}
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full" style={{ background: itemSwatch(item) }} />
                  )}
                  {item.category_name && (
                    <span
                      className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full text-[10.5px] font-medium tracking-[0.08em] uppercase"
                      style={{ background: 'color-mix(in srgb, var(--bg, #FAFAF9) 85%, transparent)', backdropFilter: 'blur(8px)' }}
                    >
                      {item.category_name}
                    </span>
                  )}
                </div>

                {/* Meta */}
                <div className="flex flex-col gap-2 p-[18px]">
                  <div className="font-semibold text-[17px] leading-[1.25] tracking-[-0.01em] text-stone-950 dark:text-zinc-50">
                    {item.name}
                  </div>
                  <div className="font-mono text-[12.5px] text-stone-500 dark:text-zinc-400">
                    {itemDim(item)}
                  </div>
                  <div className="flex items-center justify-between pt-2 mt-1 border-t border-stone-200 dark:border-zinc-800">
                    <span className="inline-flex items-center gap-[5px] text-[11px] font-medium tracking-[0.06em] uppercase text-stone-500 dark:text-zinc-400">
                      <span
                        className="w-1.5 h-1.5 rounded-full shrink-0"
                        style={{ background: stocked ? 'var(--success, #22c55e)' : 'var(--danger, #ef4444)' }}
                      />
                      {stocked ? T.inStock : T.outOfStock}
                    </span>
                    {stocked && (
                      <span className="font-mono font-medium text-[13.5px] text-stone-950 dark:text-zinc-50">
                        {item.current_stock} {item.unit}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Contact CTA */}
      <div className="py-8 border-t border-stone-200 dark:border-zinc-800 flex flex-col sm:flex-row items-center justify-between gap-4">
        <a
          href={`mailto:?subject=${encodeURIComponent(workspaceName)}`}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-[10px] bg-stone-950 dark:bg-zinc-50 text-stone-50 dark:text-zinc-950 text-[14px] font-semibold hover:opacity-90 transition-opacity"
        >
          {T.contact}
        </a>
        <a
          href="https://lithosbase.app"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[12px] text-stone-400 dark:text-zinc-500 hover:text-stone-600 dark:hover:text-zinc-300 transition-colors"
        >
          {T.poweredBy} <span className="font-semibold">Lithosbase</span>
        </a>
      </div>
    </div>
  );
}
